import {
    MoneyMapStatus,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { orderBy } from 'lodash';

import { useGetActiveMoneyMapWithIncomeSplit } from './useGetActiveMoneyMapWithIncomeSplit';
import { useGetMoneyMapsForMembership } from './useGetMoneyMapsForMembership';

const findMapById = (
    id: string | null | undefined,
    maps: Array<MoneyMapWithRulesAndAccounts | undefined>,
): MoneyMapWithRulesAndAccounts | undefined =>
    id == null ? undefined : maps.find((map) => map?.id === id);

export const useMembershipMoneyMaps = () => {
    const {
        data: activeWithSplit,
        isLoading: isLoadingActiveWithSplit,
        isError: hasMoneyMapError,
    } = useGetActiveMoneyMapWithIncomeSplit({});
    const {
        data: previewActiveAndInactiveMapsData,
        isError: isPreviewMapsError,
    } = useGetMoneyMapsForMembership({
        status: [
            MoneyMapStatus.PREVIEW,
            MoneyMapStatus.ACTIVE,
            MoneyMapStatus.INACTIVE, // Needed to determine if previous MMs exist
        ],
    });
    const previewActiveAndInctiveMaps = previewActiveAndInactiveMapsData ?? [];
    const isLoadingPreviewActiveAndInctiveMaps =
        previewActiveAndInactiveMapsData == null && !isPreviewMapsError;

    const previewMoneyMap = orderBy(
        previewActiveAndInctiveMaps.filter(
            (map) => map.status === MoneyMapStatus.PREVIEW,
        ),
        'updatedAt',
        'desc',
    )[0];

    const activeFromList = previewActiveAndInctiveMaps.find(
        (map) => map.status === MoneyMapStatus.ACTIVE,
    );

    const previewAndActiveMaps = previewActiveAndInctiveMaps.filter(
        (m) => m.status !== MoneyMapStatus.INACTIVE,
    );

    const inactiveMoneyMaps = previewActiveAndInctiveMaps.filter(
        (m) => m.status === MoneyMapStatus.INACTIVE,
    );

    const workflowFromList =
        activeFromList ??
        previewMoneyMap ??
        orderBy(previewAndActiveMaps, 'updatedAt', 'desc')[0];
    const workflowMoneyMap: MoneyMapWithRulesAndAccounts | undefined =
        isLoadingPreviewActiveAndInctiveMaps
            ? (activeWithSplit?.moneyMap ?? workflowFromList)
            : workflowFromList;

    const currentIncomeSplit = activeWithSplit?.currentIncomeSplit ?? null;
    const liveSplitMoneyMapId = currentIncomeSplit?.moneyMapId ?? null;
    const liveSplitMoneyMap: MoneyMapWithRulesAndAccounts | undefined =
        liveSplitMoneyMapId == null
            ? undefined
            : (findMapById(liveSplitMoneyMapId, [
                  workflowMoneyMap,
                  ...previewAndActiveMaps,
              ]) ??
              activeWithSplit?.liveSplitMoneyMap ??
              undefined);

    const previousMoneyMaps = inactiveMoneyMaps.filter(
        (map) => map.id !== liveSplitMoneyMapId,
    );

    const isLoading =
        (activeWithSplit == null && isLoadingActiveWithSplit) ||
        isLoadingPreviewActiveAndInctiveMaps;

    const findLoadedMoneyMap = (
        moneyMapId: string,
    ): MoneyMapWithRulesAndAccounts | undefined =>
        findMapById(moneyMapId, [
            workflowMoneyMap,
            liveSplitMoneyMap,
            ...previewAndActiveMaps,
        ]);

    return {
        workflowMoneyMap,
        previewMoneyMap,
        liveSplitMoneyMap,
        currentIncomeSplit,
        findLoadedMoneyMap,
        areSame:
            workflowMoneyMap != null &&
            workflowMoneyMap.id === liveSplitMoneyMap?.id,
        isLoading,
        previewAndActiveMaps,
        isLoadingPreviewActiveAndInctiveMaps,
        hasMoneyMapError,
        hasPreviousMoneyMaps: previousMoneyMaps.length > 0,
    };
};
